/**
 * DecibelMeter Component
 *
 * Real-time audio level visualization for browser recording.
 * Displays a horizontal bar meter with color-coded levels:
 * - Green: Normal (good recording level)
 * - Yellow: Quiet (may need to speak louder)
 * - Red: Loud/Clipping (too loud, may distort)
 *
 * Uses CSS transitions for smooth animation.
 */

import { cn } from "@/lib/utils";
import type { AudioLevelData } from "@/types/browser-recording";

interface DecibelMeterProps {
  /** Current audio level data */
  audioLevel: AudioLevelData;
  /** Optional className for the container */
  className?: string;
  /** Show level labels below the meter */
  showLabels?: boolean;
  /** Show numeric decibel value */
  showDecibels?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
}

/**
 * Get the color class based on audio level
 */
function getLevelColorClass(level: AudioLevelData["level"]): string {
  switch (level) {
    case "quiet":
      return "bg-yellow-500 dark:bg-yellow-600";
    case "normal":
      return "bg-green-500 dark:bg-green-600";
    case "loud":
      return "bg-green-600 dark:bg-green-700";
    case "clipping":
      return "bg-red-500 dark:bg-red-600";
    default:
      return "bg-green-500 dark:bg-green-600";
  }
}

/**
 * Get the background gradient for the meter
 */
function getMeterBackground(): string {
  return "bg-gradient-to-r from-yellow-900/20 via-green-900/20 to-red-900/20";
}

/**
 * Get size-specific classes
 */
function getSizeClasses(size: DecibelMeterProps["size"]) {
  switch (size) {
    case "sm":
      return {
        container: "h-2",
        text: "text-xs",
      };
    case "lg":
      return {
        container: "h-6",
        text: "text-base",
      };
    case "md":
    default:
      return {
        container: "h-4",
        text: "text-sm",
      };
  }
}

export function DecibelMeter({
  audioLevel,
  className,
  showLabels = true,
  showDecibels = false,
  size = "md",
}: DecibelMeterProps) {
  const sizeClasses = getSizeClasses(size);

  // Calculate meter width (normalized value is 0-1)
  const meterWidth = Math.min(100, audioLevel.normalized * 100);

  // Calculate peak indicator position
  const peakPosition = Math.min(100, audioLevel.peak * 100);

  return (
    <div className={cn("space-y-1", className)}>
      {/* Meter container */}
      <div
        className={cn(
          "relative w-full rounded-full overflow-hidden",
          getMeterBackground(),
          sizeClasses.container
        )}
      >
        {/* Level bar */}
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full transition-all duration-75",
            getLevelColorClass(audioLevel.level)
          )}
          style={{ width: `${meterWidth}%` }}
        />

        {/* Peak indicator */}
        <div
          className="absolute inset-y-0 w-0.5 bg-card/70 transition-all duration-150"
          style={{ left: `${peakPosition}%` }}
        />

        {/* Threshold markers */}
        <div className="absolute inset-0 flex">
          {/* Quiet/Normal threshold (10%) */}
          <div
            className="absolute h-full w-px bg-card/20"
            style={{ left: "10%" }}
          />
          {/* Normal/Loud threshold (50%) */}
          <div
            className="absolute h-full w-px bg-card/20"
            style={{ left: "50%" }}
          />
          {/* Loud/Clipping threshold (90%) */}
          <div
            className="absolute h-full w-px bg-card/20"
            style={{ left: "90%" }}
          />
        </div>
      </div>

      {/* Labels */}
      {showLabels && (
        <div
          className={cn(
            "flex justify-between text-muted-foreground",
            sizeClasses.text
          )}
        >
          <span>Quiet</span>
          <span>Normal</span>
          <span>Loud</span>
        </div>
      )}

      {/* Decibel value */}
      {showDecibels && (
        <div
          className={cn("text-center text-muted-foreground", sizeClasses.text)}
        >
          {audioLevel.decibels > -100
            ? `${audioLevel.decibels.toFixed(1)} dB`
            : "No signal"}
        </div>
      )}
    </div>
  );
}

/**
 * Compact version of the decibel meter for inline use
 */
export function DecibelMeterCompact({
  audioLevel,
  className,
}: Pick<DecibelMeterProps, "audioLevel" | "className">) {
  const meterWidth = Math.min(100, audioLevel.normalized * 100);

  return (
    <div
      className={cn(
        "relative h-1 w-16 rounded-full bg-muted overflow-hidden",
        className
      )}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 rounded-full transition-all duration-75",
          getLevelColorClass(audioLevel.level)
        )}
        style={{ width: `${meterWidth}%` }}
      />
    </div>
  );
}
